import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDropdownPlacement } from "./use-dropdown-placement";

export type ComboboxOption = {
  value: string;
  label: string;
  /** Optional secondary text shown to the right of the label. */
  hint?: string;
  /** Render the label struck-through + dimmed (e.g. a recently-closed
   *  work item that's still assignable during its grace window). The
   *  option stays fully selectable. */
  struck?: boolean;
  /** Dim the option and refuse to commit it — the native `<option
   *  disabled>` equivalent (e.g. a status a suggestion can't move to
   *  without an assignee yet). */
  disabled?: boolean;
};

type Props = {
  options: ComboboxOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Form name — renders a hidden input so the combobox plugs into a `<form>`. */
  name?: string;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  className?: string;
  size?: "default" | "sm";
  "aria-label"?: string;
  "data-testid"?: string;
  /** false renders a plain listbox with no search box — for the fixed,
   *  short enum lists that used to be a native `<select>`. Native
   *  selects were dropped app-wide because Chrome now resolves a
   *  styled `<select>`'s popup as a press-drag-release menu: a plain
   *  click (down+up with no drag) commits whatever option the cursor
   *  lands on, instead of just opening the list. Default true keeps
   *  every existing entity-picker call site unchanged. */
  searchable?: boolean;
};

/**
 * Single-select, searchable by default. Verbatim port of the
 * Next-side `combobox.tsx` (`"use client"` dropped), later widened
 * with `searchable={false}` to also cover the fixed-enum lists that
 * used to be a native `<select>` (see that prop's doc comment).
 *
 * Mirrors native-select muscle memory: click opens (+ focuses the
 * search box when searchable), type filters, arrows move the
 * highlight, Enter selects, Esc / outside-click closes.
 */
export function Combobox({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  name,
  placeholder = "— select —",
  emptyLabel = "No matches",
  disabled,
  loading,
  clearable,
  className,
  size = "default",
  "aria-label": ariaLabel,
  "data-testid": testId,
  searchable = true,
}: Props) {
  const [uncontrolled, setUncontrolled] = useState<string>(defaultValue ?? "");
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolled;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  // Search box (~2.5rem, skipped when !searchable) + the list's
  // max-h-64 (16rem) ≈ 19rem, or ≈16.5rem without the search box.
  const placement = useDropdownPlacement(wrapperRef, open, searchable ? 304 : 264);

  // The panel portals to <body> (see the render below) so it always paints
  // above page content regardless of which stacking context it opens from —
  // a Select inside one `fixed` bar of several stacked floating bars (e.g.
  // the time-entries bulk-action bars) would otherwise render its panel
  // behind a *later* sibling bar: z-index only wins within a shared
  // stacking context, and two `fixed` siblings each start their own.
  // Portaling needs its own pixel coordinates since it leaves the trigger's
  // `position: relative` wrapper behind.
  // Inside a modal the panel goes into the dialog itself, not <body>: a
  // dialog traps focus, and a panel outside that trap has the keyboard
  // taken back from it the moment it opens — the search box could be
  // clicked and never typed into. Everywhere else it still portals to
  // <body> for the stacking reasons above.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const dialog = wrapperRef.current?.closest<HTMLElement>("[role='dialog']");
    setPortalTarget(dialog ?? document.body);
  }, [open]);

  const [coords, setCoords] = useState<{
    left: number;
    minWidth: number;
    maxWidth: number;
    top?: number;
    bottom?: number;
  }>();
  useLayoutEffect(() => {
    if (!open) return;
    function measure() {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      // minWidth (not width) lets the panel grow past the trigger to fit
      // whichever option has the longest label, instead of mirroring the
      // trigger's own width — which shrinks to fit the *currently selected*
      // option and would truncate every longer one in the list.
      // A dialog is positioned with a transform, which makes `fixed`
      // inside it resolve against the dialog rather than the viewport —
      // so coordinates measured off the viewport have to come back to
      // the container's own frame.
      const host = portalTarget && portalTarget !== document.body ? portalTarget : null;
      const origin = host?.getBoundingClientRect();
      const base = {
        left: rect.left - (origin?.left ?? 0),
        minWidth: rect.width,
        maxWidth: window.innerWidth - rect.left - 8,
      };
      const viewportHeight = host ? (origin?.height ?? window.innerHeight) : window.innerHeight;
      setCoords(
        placement === "up"
          ? { ...base, bottom: viewportHeight - (rect.top - (origin?.top ?? 0)) + 4 }
          : { ...base, top: rect.bottom - (origin?.top ?? 0) + 4 },
      );
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, placement, portalTarget]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label} ${o.hint ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  // Reset the search + highlight and focus the input ONLY when the
  // dropdown opens — not when `options` / `value` change identity. A
  // parent that re-renders while the dropdown is open (e.g. the timer
  // widget's 1s elapsed tick) passes a fresh inline `options` array
  // each render; depending on it here would wipe the user's query on
  // every keystroke, so search appears broken inside such modals.
  // biome-ignore lint/correctness/useExhaustiveDependencies: open-transition only — options/value are read at open time on purpose
  useEffect(() => {
    if (open) {
      setQuery("");
      const idx = options.findIndex((o) => o.value === value);
      setHighlight(idx >= 0 ? idx : 0);
      if (searchable) requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      const target = ev.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      // The panel is portaled to <body> — it's not a DOM descendant of
      // wrapperRef, so it needs its own check or every click inside it
      // would look like an outside click.
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function commit(next: string) {
    if (!isControlled) setUncontrolled(next);
    onChange?.(next);
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt && !opt.disabled) commit(opt.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  const triggerHeight = size === "sm" ? "h-7 text-xs" : "h-9 text-sm";

  return (
    // min-w-28 floor: as a flex-row child (many call sites drop it into one
    // with no width class), the trigger otherwise shrinks to fit whichever
    // label is *currently selected* — collapsing to near-nothing for a
    // short value like "—". `className` can still widen or override it.
    <div ref={wrapperRef} className={cn("relative min-w-28", className)}>
      {name && <input type="hidden" name={name} value={value} />}

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKey}
        data-value={value}
        data-testid={testId}
        // biome-ignore lint/a11y/useSemanticElements: this IS the replacement for a native <select> (see the `searchable` prop doc comment) — a real <select> is exactly what it can't be
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        className={cn(
          "flex w-full items-center justify-between gap-1.5 rounded-md border border-input bg-background px-2 py-1",
          "focus:outline-none focus:ring-2 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          triggerHeight,
        )}
      >
        <span className={cn("truncate text-left", !selected && "text-muted-foreground")}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0 text-muted-foreground">
          {clearable && value && !disabled && (
            // biome-ignore lint/a11y/useSemanticElements: a nested <button> is invalid HTML — the outer trigger is already a <button>
            // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard activation happens on the outer button; the X is mouse-only by design
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              onClick={(ev) => {
                ev.stopPropagation();
                commit("");
              }}
              className="rounded hover:bg-accent hover:text-accent-foreground p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5" />
        </span>
      </button>

      {open &&
        coords &&
        portalTarget &&
        createPortal(
          <div
            ref={panelRef}
            // Marks the panel as belonging to whatever opened it. It is
            // portalled to <body>, so a modal's focus trap would otherwise
            // treat it as "outside" and pull the keyboard back — see
            // DialogContent, which lets focus and pointer events through to
            // anything carrying this.
            data-combobox-panel=""
            style={{
              position: "fixed",
              left: coords.left,
              minWidth: coords.minWidth,
              maxWidth: coords.maxWidth,
              top: coords.top,
              bottom: coords.bottom,
            }}
            // z-60 keeps it visually above a Dialog's z-50. pointer-events-auto
            // is load-bearing: Radix's modal Dialog sets `body { pointer-events:
            // none }` while open and only opts its own content element back in —
            // this panel is a <body> sibling, not a descendant, so without the
            // override every click on it silently fell through to whatever the
            // dialog painted underneath instead of registering on the option.
            className="z-[60] pointer-events-auto overflow-hidden rounded-md border border-input bg-popover shadow-lg"
            // Same "body sibling, not descendant" story for wheel/touch: Radix's
            // scroll lock (react-remove-scroll) preventDefault()s any wheel/touch
            // event at the document level unless it originated inside the
            // dialog's own tracked content — stopping propagation here keeps it
            // from ever reaching that document listener, so the list's own
            // native overflow-y scroll goes through untouched.
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {searchable && (
              <div className="border-b border-input p-1.5">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlight(0);
                  }}
                  onKeyDown={onKey}
                  placeholder="Search…"
                  className="flex h-7 w-full rounded-sm bg-transparent px-1.5 text-sm focus:outline-none"
                />
              </div>
            )}
            {/* The trigger points here with `aria-controls` and says it
                opens a listbox — so this has to be one, or a screen
                reader is told about a list it then can't find. */}
            <ul
              id={listId}
              // biome-ignore lint/a11y/useSemanticElements: a native <select> is exactly what this replaces — see the `searchable` prop doc comment
              // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: the trigger's aria-controls points here and promises a listbox
              role="listbox"
              aria-label={ariaLabel}
              // Focus stays on the trigger (or the search box); the list
              // is reachable programmatically, which is what the rule asks
              // for without stealing the keyboard from the combobox.
              tabIndex={-1}
              className="max-h-64 overflow-y-auto py-1"
            >
              {loading ? (
                <li className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading…
                </li>
              ) : filtered.length === 0 ? (
                <li className="px-2 py-2 text-sm text-muted-foreground italic">{emptyLabel}</li>
              ) : (
                filtered.map((o, i) => {
                  const isSel = o.value === value;
                  const isHi = i === highlight;
                  return (
                    <li
                      key={o.value}
                      data-value={o.value}
                      // biome-ignore lint/a11y/useSemanticElements: <option> is only valid inside a native <select>; this is a custom listbox
                      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: a custom listbox option carries the "option" role by design
                      role="option"
                      tabIndex={-1}
                      aria-selected={isSel}
                      aria-disabled={o.disabled}
                      onMouseEnter={() => !o.disabled && setHighlight(i)}
                      onMouseDown={(ev) => {
                        // mousedown so the click commits BEFORE the outside-click
                        // detector closes the panel and unmounts us.
                        ev.preventDefault();
                        if (!o.disabled) commit(o.value);
                      }}
                      className={cn(
                        "flex items-center justify-between gap-2 px-2 py-1.5 text-sm",
                        o.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                        isHi && !o.disabled && "bg-accent text-accent-foreground",
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        <Check
                          className={cn(
                            "h-3.5 w-3.5 shrink-0",
                            isSel ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className={cn("truncate", o.struck && "line-through opacity-60")}>
                          {o.label}
                        </span>
                      </span>
                      {o.hint && (
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                          {o.hint}
                        </span>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          portalTarget,
        )}
    </div>
  );
}
